import type { AuthResponse, LoginRequest, User, Permission } from "../../types/auth";
import { apiClient } from "../core/apiClient";
import { validateUserRole } from "../../utils/roleValidation";

export class AuthService {
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    try {
      const response = await apiClient.post<AuthResponse>('/auth/login', credentials, { 
        skipAuth: true 
      });
      
      // El backend envía la estructura correcta, solo necesitamos mapearla
      // La respuesta viene como: { data: { id, email, full_name, username, role: { id, role_name, description }, token } }
      const loginData = response.data;
      
      // Mapear la respuesta del backend al formato esperado por el frontend
      const mappedResponse = {
        id: loginData.id,
        token: loginData.token,
        full_name: loginData.full_name,
        email: loginData.email,
        username: loginData.username,
        roleUser: loginData.role?.id || 1,
        role: loginData.role ? {
          id: loginData.role.id,
          role_name: loginData.role.role_name,
          permissions: [] // Los permisos se obtendrán por separado si es necesario
        } : {
          id: 1,
          role_name: 'admin',
          permissions: []
        }
      };

      return mappedResponse;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Error de conexión');
    }
  }

  async logout(): Promise<void> {
    try {
      await apiClient.post('/auth/logout');
    } catch (error) {
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
    }
  }

  async getCurrentUser(): Promise<User> {
    try {
      const response = await apiClient.get('/auth/me');
      // El backend responde: { user: { id, email, full_name, username, role: { id, role_name, description } } }
      const userData = response.data.user;
      
      // Obtener permisos del rol
      let permissions: Permission[] = [];
      
      if (userData.role?.id) {
        try {
          const permissionsResponse = await apiClient.get(`/permissions/role/${userData.role.id}`);

          
          const roleWithPermissions = permissionsResponse.data;

          
          if (roleWithPermissions.permissions && Array.isArray(roleWithPermissions.permissions)) {
            permissions = roleWithPermissions.permissions.map((p: any) => p.name);
          }

        } catch (permError) {
          
          // Usar permisos por defecto del rol
          permissions = this.getDefaultPermissionsForRole(userData.role.role_name);
        }
      } else {
        permissions = this.getDefaultPermissionsForRole('user');
      }
      
      // Asegurar que el usuario tenga la estructura correcta
      const roleName = userData.role?.role_name || 'admin';
      const validRole = validateUserRole(roleName);
      
      const user: User = {
        id: userData.id?.toString() || '1',
        name: userData.full_name || userData.name || 'Usuario',
        email: userData.email || 'user@example.com',
        role: validRole,
        status: 'active',
        permissions: permissions,
        roleId: userData.role?.id || 1,
        roleName: roleName,
        lastLogin: undefined
      };


      return user;
    } catch (error) {
      
      // Si falla, intentar obtener datos básicos del localStorage
      const savedUser = localStorage.getItem('user');
      if (savedUser) {
        try {
          return JSON.parse(savedUser);
        } catch (parseError) {
        }
      }
      throw new Error(error instanceof Error ? error.message : 'Error al obtener usuario');
    }
  }

  // Método auxiliar para obtener permisos por defecto de un rol
  private getDefaultPermissionsForRole(roleName: string): Permission[] {
    const rolePermissions: Record<string, Permission[]> = {
      'superadmin': [
        'dashboard.view', 'leads.view', 'leads.create', 'leads.edit', 'leads.delete',
        'deals.view', 'deals.create', 'deals.edit', 'deals.delete',
        'contacts.view', 'contacts.create', 'contacts.edit', 'contacts.delete',
        'users.view', 'users.create', 'users.edit', 'users.delete',
        'funcionarios.view', 'funcionarios.create', 'funcionarios.edit', 'funcionarios.delete',
        'eventos.view', 'eventos.create', 'eventos.edit', 'eventos.delete',
        'accesos.view', 'accesos.create', 'accesos.edit', 'accesos.delete',
        'reports.view', 'reports.export', 'configuracion.view', 'configuracion.edit',
        'callcenter.view', 'callcenter.create', 'callcenter.edit', 'puertas.view', 'puertas.control'
      ],
      'admin': [
        'dashboard.view', 'leads.view', 'leads.create', 'leads.edit', 'leads.delete',
        'deals.view', 'deals.create', 'deals.edit', 'deals.delete',
        'contacts.view', 'contacts.create', 'contacts.edit', 'contacts.delete',
        // 'users.view', 'users.create', 'users.edit',
        // 'funcionarios.view', 'funcionarios.create', 'funcionarios.edit', 'funcionarios.delete',
        // 'eventos.view', 'eventos.create', 'eventos.edit', 'eventos.delete',
        // 'accesos.view', 'accesos.create', 'accesos.edit', 'accesos.delete',
        // 'reports.view', 'reports.export', 'configuracion.view',
        // 'callcenter.view', 'callcenter.create', 'callcenter.edit', 'puertas.view', 'puertas.control'
      ],
      'manager': [
        'dashboard.view', 'leads.view', 'leads.create', 'leads.edit',
        'deals.view', 'deals.create', 'deals.edit', 'contacts.view', 'contacts.create', 'contacts.edit',
        'users.view', 'funcionarios.view', 'funcionarios.create', 'funcionarios.edit',
        'eventos.view', 'eventos.create', 'eventos.edit', 'accesos.view', 'accesos.create', 'accesos.edit',
        'reports.view', 'reports.export', 'callcenter.view', 'callcenter.create', 'callcenter.edit', 'puertas.view'
      ],
      'agent': [
        'dashboard.view', 'leads.view', 'leads.create', 'leads.edit',
        'deals.view', 'deals.create', 'deals.edit', 'contacts.view', 'contacts.create', 'contacts.edit',
        'eventos.view', 'eventos.create', 'callcenter.view', 'callcenter.create', 'callcenter.edit'
      ],
      'viewer': [
        'dashboard.view', 'leads.view', 'deals.view', 'contacts.view', 'funcionarios.view',
        'eventos.view', 'accesos.view', 'reports.view', 'callcenter.view', 'puertas.view'
      ],
      'user': [
        'dashboard.view', 'leads.view', 'leads.create', 'leads.edit',
        'deals.view', 'deals.create', 'deals.edit', 'contacts.view', 'contacts.create', 'contacts.edit',
        'eventos.view', 'eventos.create', 'callcenter.view', 'callcenter.create', 'callcenter.edit'
      ]
    };
    
    return rolePermissions[roleName] || rolePermissions['user'];
  }

  async getUserRole(roleId: number): Promise<{ id: number; name: string; permissions: Permission[] }> {
    try {
      // Usar el endpoint específico para obtener permisos del rol
      const response = await apiClient.get(`/permissions/role/${roleId}`);
      const roleData = response.data;
      
      const permissions = roleData.permissions?.map((p: any) => p.name) || [];

      return {
        id: roleData.id,
        name: roleData.role_name,
        permissions: permissions
      };
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Error de conexión');
    }
  }

  async updateUserPermissions(userId: string, permissions: Permission[]): Promise<void> {
    try {
      // Este endpoint no existe en tu backend actual, usar el de actualizar rol
      await apiClient.put(`/user/${userId}/permissions`, { permissions });
      throw new Error('Endpoint no implementado en el backend');
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Error al actualizar permisos');
    }
  }

  async getAllRoles(): Promise<Array<{ id: number; name: string; permissions: Permission[] }>> {
    try {
      // El backend responde: { data: { roles: [...], pagination: {...} } }
      const response = await apiClient.get('/roles');
      const rolesData = response.data.data.roles;
      
      // Para cada rol, obtener sus permisos
      const rolesWithPermissions = await Promise.all(
        rolesData.map(async (role: any) => {
          try {
            const permissionsResponse = await apiClient.get(`/permissions/role/${role.id}`);
            const roleWithPermissions = permissionsResponse.data;
            const permissions = roleWithPermissions.permissions?.map((p: any) => p.name) || [];
            
            return {
              id: role.id,
              name: role.role_name,
              permissions: permissions
            };
          } catch (error) {
            // Si falla obtener permisos, usar permisos por defecto
            return {
              id: role.id,
              name: role.role_name,
              permissions: this.getDefaultPermissionsForRole(role.role_name)
            };
          }
        })
      );
      
      return rolesWithPermissions;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Error al obtener roles');
    }
  }

  // async changePassword(oldPassword: string, newPassword: string): Promise<void> {
  //   await apiClient.post('/auth/change-password', {
  //     oldPassword,
  //     newPassword
  //   });
  // }

  // async requestPasswordReset(email: string): Promise<void> {
  //   await apiClient.post('/auth/forgot-password', { email }, { skipAuth: true });
  // }
}

export const authService = new AuthService();
